import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Grid,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import loginApi from "../../Services/Login";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../Redux/SnackBar/actions";
import { showLoader, hideLoader } from "../../Redux/Loader/actions";
import { setNewSiteLogo } from "Redux/SiteSettings/actions";
import { setNewtimezone, setSystemlatitude, setSystemlongitude } from "Redux/TimeZone/actions";
import { setAuthentication } from "../../Redux/Authentication/actions";
import settingsApi from "Services/Settings";

// Storefront SVG icon for Food Admin button matching the screenshot
const StorefrontIconSvg = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l2-5h14l2 5" />
    <path d="M21 9v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9" />
    <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
    <path d="M9 22V12h6v10" />
  </svg>
);

const PORTAL_ROLES = [
  { key: "ADMIN", label: "Admin", icon: "/assets/images/login_icons/ADMIN.png" },
  { key: "ACCOUNT MANAGER", label: "Account Manager", icon: "/assets/images/login_icons/ACCOUNT MANAGER.png" },
  { key: "DISPATCHER", label: "Dispatcher", icon: "/assets/images/login_icons/DISPATCHER.png" },
  { key: "SUB COMPANY", label: "Sub Company", icon: "/assets/images/login_icons/SUB COMPANY.png" },
  { key: "CORPORATE", label: "Corporate", icon: "/assets/images/login_icons/CORPORATE.png" },
  { key: "HOTEL", label: "Hotel", icon: "/assets/images/login_icons/HOTEL.png" },
];

const Login = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [selectedOption, setSelectedOption] = useState("ADMIN");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem("unicodel_remembered_email");
    if (savedEmail) {
      formik.setFieldValue("email", savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleRoleClick = (roleKey) => {
    setSelectedOption(roleKey);
  };

  const handleLogin = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    if (rememberMe) {
      localStorage.setItem("unicodel_remembered_email", formValues.email);
    } else {
      localStorage.removeItem("unicodel_remembered_email");
    }

    const data = {
      email: formValues?.email,
      password: formValues?.password,
      userType: selectedOption,
    };

    const onSuccess = (res) => {
      dispatch(hideLoader());
      dispatch(setAuthentication(res?.data?.data));
      dispatch(
        showSnackbar({
          message: res?.data?.message || "Logged in successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );
      getMasterValues();
      getBusinessValues();
      navigate("/");
    };

    const onFailure = (err) => {
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while logging in",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      dispatch(hideLoader());
    };

    loginApi.login({ ...data }).then(onSuccess, onFailure);
  };

  const getMasterValues = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(setNewSiteLogo(res?.data?.data?.siteLogo));
      }
    };
    const onFailure = () => {
      dispatch(hideLoader());
    };
    settingsApi.getMasterValues().then(onSuccess, onFailure);
  }, [dispatch]);

  const getBusinessValues = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(setNewtimezone(res?.data?.data?.timeZone));
        dispatch(setSystemlatitude(res?.data?.data?.latitude));
        dispatch(setSystemlongitude(res?.data?.data?.longitude));
      }
    };
    const onFailure = () => {
      dispatch(hideLoader());
    };
    settingsApi.getBusinessSettings().then(onSuccess, onFailure);
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      email: yup.string().required("Email is required").nullable(),
      password: yup.string().required("Password is required").nullable(),
    }),
    onSubmit: handleLogin,
  });

  const handleForgotPassword = () => {
    dispatch(
      showSnackbar({
        message: "Please contact your system administrator to reset password.",
        autoHideDuration: 4000,
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        variant: "info",
      })
    );
  };

  return (
    <div className={classes.root}>
      {/* Top Left Hero Branding */}
      <div className={classes.heroBranding}>
        <Typography className={classes.heroTagline}>
          Kick-off Your Business With
        </Typography>
        <Typography className={classes.heroTitle}>
          R8de
        </Typography>
      </div>

      {/* Floating Login Card */}
      <div className={classes.loginCard}>
        <form onSubmit={formik.handleSubmit}>
          {/* Card Header */}
          <div className={classes.logoContainer}>
            <img
              src="/assets/images/logos/r8de.png"
              alt="r8de.png"
              className={classes.logoImg}
            />
          </div>

          <Typography className={classes.cardSubtitle}>
            Kick-off Your Business with{" "}
            <span className={classes.brandHighlight}>R8de</span>
          </Typography>

          {/* Divider: SIGN IN TO DASHBOARD */}
          <div className={classes.dividerWrapper}>
            <div className={classes.dividerLine} />
            <span className={classes.dividerText}>SIGN IN TO DASHBOARD</span>
            <div className={classes.dividerLine} />
          </div>

          {/* Role Selection */}
          <Box sx={{ mb: 2.2 }}>
            <Typography className={classes.sectionLabel}>
              SELECT PORTAL ROLE
            </Typography>

            <Grid container spacing={1}>
              {PORTAL_ROLES.map((role) => {
                const isSelected = selectedOption === role.key;
                return (
                  <Grid item xs={4} key={role.key}>
                    <Button
                      fullWidth
                      onClick={() => handleRoleClick(role.key)}
                      className={classes.rolePillButton}
                      sx={{
                        border: isSelected
                          ? "1px solid #000000 !important"
                          : "1px solid #e2e8f0 !important",
                        backgroundColor: isSelected
                          ? "#000000 !important"
                          : "#ffffff !important",
                        color: isSelected
                          ? "#ffffff !important"
                          : "#1e293b !important",
                        boxShadow: isSelected
                          ? "0 2px 8px rgba(0, 0, 0, 0.15) !important"
                          : "none !important",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? "#1a1a1a !important"
                            : "#f8fafc !important",
                          borderColor: isSelected
                            ? "#000000 !important"
                            : "#cbd5e1 !important",
                        },
                      }}
                    >
                      <img
                        src={role.icon}
                        alt={role.label}
                        style={{
                          width: 17,
                          height: 17,
                          objectFit: "contain",
                          filter: isSelected ? "brightness(0) invert(1)" : "none",
                        }}
                      />
                      <span>{role.label}</span>
                    </Button>
                  </Grid>
                );
              })}

              {/* Food Admin Login Pill Button */}
              {/* <Grid item xs={12} sx={{ mt: 0.2 }}>
                <Button
                  onClick={() => handleRoleClick("FOOD ADMIN")}
                  className={`${classes.foodAdminButton} ${
                    selectedOption === "FOOD ADMIN" ? classes.foodAdminActive : ""
                  }`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color:
                        selectedOption === "FOOD ADMIN" ? "#ffffff" : "#334155",
                    }}
                  >
                    <StorefrontIconSvg />
                  </Box>
                  <span>Food Admin login</span>
                  <ArrowForwardIcon sx={{ fontSize: 13, ml: 0.3 }} />
                </Button>
              </Grid> */}
            </Grid>
          </Box>

          {/* Email Address */}
          <Box sx={{ mb: 1.8 }}>
            <Typography className={classes.sectionLabel}>
              EMAIL ADDRESS
            </Typography>
            <TextField
              className={classes.inputField}
              id="email"
              name="email"
              type="text"
              placeholder="operations@r8de.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1, color: "#94a3b8" }}>
                    <MailOutlineIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Password */}
          <Box sx={{ mb: 1 }}>
            <div className={classes.fieldLabelRow}>
              <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
                PASSWORD
              </Typography>
              <div
                className={classes.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <>
                    <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                    <span>Hide</span>
                  </>
                ) : (
                  <>
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 17 }} />
                    <span>Show</span>
                  </>
                )}
              </div>
            </div>

            <TextField
              className={classes.inputField}
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="un1c0ltaxiSecure"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1, color: "#94a3b8" }}>
                    <LockOutlinedIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Remember Me and Forgot Password */}
          <div className={classes.optionsRow}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: "#cbd5e1",
                    p: 0.5,
                    "&.Mui-checked": {
                      color: "#0f172a",
                    },
                  }}
                />
              }
              label={
                <span className={classes.rememberLabel}>
                  Remember me
                </span>
              }
              sx={{ m: 0 }}
            />

            <span
              className={classes.forgotPasswordLink}
              onClick={handleForgotPassword}
            >
              Forgot Password <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </span>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            className={classes.submitButton}
          >
            Sign In <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
